const API_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_URL) ||
  "http://localhost:3000/api";

class ApiService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private getToken(): string | null {
    return localStorage.getItem("accessToken");
  }

  private setToken(token: string): void {
    localStorage.setItem("accessToken", token);
  }

  private removeToken(): void {
    localStorage.removeItem("accessToken");
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>)["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: "include",
      });

      const text = await response.text();
      
      if (!text || text.trim() === "") {
        throw new Error("Empty response from server");
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(`Invalid JSON response: ${text.substring(0, 100)}`);
      }

      if (!response.ok) {
        const errorMsg = data?.message || `Request failed with status ${response.status}`;
        throw new Error(errorMsg);
      }

      return data;
    } catch (err: any) {
      // Log the actual error for debugging
      console.error(`API Error [${endpoint}]:`, err.message);
      throw err;
     }
   }

  async getText(endpoint: string): Promise<string> {
    console.log(`getText called for endpoint: ${endpoint}`);
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, {
        method: "GET",
        headers,
        credentials: "include",
      });

      console.log(`getText response status: ${response.status} for endpoint: ${endpoint}`);

      if (!response.ok) {
        const text = await response.text();
        console.log(`getText error response text: ${text.substring(0, 200)}`);
        let errorMsg = `Request failed with status ${response.status}`;
        try {
          const data = JSON.parse(text);
          errorMsg = data?.message || errorMsg;
        } catch {
          // If we can't parse as JSON, use the raw text or default message
          errorMsg = text.substring(0, 100) || errorMsg;
        }
        throw new Error(errorMsg);
      }

      const text = await response.text();
      console.log(`getText success response text: ${text.substring(0, 200)}`);
      return text;
    } catch (err: any) {
      // Log the actual error for debugging
      console.error(`API Error [${endpoint}]:`, err.message);
      throw err;
    }
  }

   async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  async post<T>(endpoint: string, body?: unknown, init?: RequestInit): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
      ...init,
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async globalSearch<T>(query: string, options?: { limit?: number; category?: string }): Promise<T> {
    const params = new URLSearchParams({ q: query, limit: (options?.limit || 10).toString() });
    if (options?.category) params.append('category', options.category);
    return this.request<T>(`/search?${params.toString()}`);
  }

  async uploadFile<T>(endpoint: string, file: File, fieldName: string = "file"): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const token = this.getToken();

    const formData = new FormData();
    formData.append(fieldName, file);

    const headers: HeadersInit = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: formData,
      credentials: "include",
    });

    const text = await response.text();
    
    if (!text) {
      throw new Error("Empty response from server");
    }

    let data;
    try {
      data = JSON.parse(text);
    } catch {
      throw new Error(`Server error: ${response.status} - ${text.substring(0, 100)}`);
    }

    if (!response.ok) {
      const errorMsg = data.message || `Request failed with status ${response.status}`;
      throw new Error(errorMsg);
    }

    return data;
  }
}

export const api = new ApiService();